import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { TestLayout } from '@/components/learner/test'

interface SpecialLectureStartCardProps {
  setName: string
  title: string
  description: ReactNode
  count?: number
  countLabel?: string
  resumeLabel?: string
  completedCount?: number
  action?: ReactNode
}

/** Figma Test-Info / AIQ-Info-off 공통 평가 안내 카드. */
export function SpecialLectureStartCard({
  setName,
  title,
  description,
  count,
  countLabel,
  resumeLabel,
  completedCount,
  action,
}: SpecialLectureStartCardProps) {
  const progress = count && completedCount
    ? Math.min(100, Math.round((completedCount / count) * 100))
    : 0

  return (
    <TestLayout variant="card">
      <div className="flex w-full max-w-[760px] flex-col items-center gap-8 px-1 sm:gap-12 sm:px-6">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <p className="max-w-[760px] text-lc-v3-h4 font-semibold text-lc-text-link">
              {setName}
            </p>
            <h1 className="text-lc-v3-h3 font-bold text-lc-text-primary sm:text-lc-v3-h1">
              {title}
            </h1>
          </div>
          <p className="whitespace-pre-line text-lc-v3-body-1 font-medium text-lc-text-secondary">
            {description}
          </p>
          {count != null && count > 0 && countLabel ? (
            <div className="inline-flex items-center justify-center rounded-full bg-lc-key-50 px-3.5 py-2 text-lc-v3-label-lg font-medium text-lc-key-500">
              {countLabel}
            </div>
          ) : null}
          {resumeLabel ? (
            <div className="w-full max-w-[320px] rounded-2xl bg-lc-v3-gray-100 px-4 py-3 text-left">
              <div className="flex items-center gap-2 text-lc-v3-body-2 font-semibold text-lc-text-secondary">
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-lc-key-500 text-white">
                  <Check className="size-4 stroke-[3]" aria-hidden />
                </span>
                <span>{resumeLabel}</span>
              </div>
              {count && completedCount ? (
                <div
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={count}
                  aria-valuenow={completedCount}
                  className="mt-3 h-2 overflow-hidden rounded-full bg-lc-v3-gray-300"
                >
                  <div
                    className="h-full rounded-full bg-lc-key-500 transition-[width]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="w-full text-center sm:w-auto">{action}</div>
      </div>
    </TestLayout>
  )
}
